import { Injectable } from '@angular/core';

import { Partners, Quote, QUOTE_TYPES } from '@lockton-apps/shared/interfaces';
import { ConfirmDialogData } from '@lockton-apps/shared/ui/lui';
import { createDefinition } from '@lockton-apps/shared/util/helpers';
import { CobMeta, CobMetaService } from '@lockton-apps/shared/util/constants';

import { DISPATCH_ACTIONS } from '../utils/patch-actions';
import { accaTncTemplate } from '../constants/workflow-messages-templates/acca-tnc-dialog.template';

@Injectable({
  providedIn: 'root',
})
export class WorkflowMessagesService {
  private readonly map = createDefinition<{
    create: (quote: Quote, cobMeta: CobMeta | undefined) => ConfirmDialogData;
  }>({
    base: {},
    variants: {
      [DISPATCH_ACTIONS.SendRenewalInvite]: {
        base: {
          create(quote: Quote) {
            if (quote.type === QUOTE_TYPES.Renewal) {
              return {
                title: $localize`Send Renewal Invite`,
                content: $localize`You are about to send a renewal invite to <span class="fw-b">${quote.clientName}</span>. The client will receive an email informing them the renewal proposal form is ready for them to edit and submit.`,
                confirmButtonText: $localize`Send Invite`,
                cancelButtonText: $localize`Cancel`,
              };
            }

            return {
              title: $localize`Send Invite`,
              content: $localize`You are about to send an invite to <span class="fw-b">${quote.clientName}</span>. The client will receive an email informing them the proposal form is ready for them to edit and submit.`,
              confirmButtonText: $localize`Send Invite`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
      },
      [DISPATCH_ACTIONS.Send]: {
        base: {
          create(quote: Quote) {
            return {
              title: $localize`Send quote to client`,
              content: $localize`You are about to inform <span class="fw-b">${quote.clientName}</span> their quote is ready to view. The client will receive an email informing them the quotation documents are uploaded into the policy quotes folder ready for them to view and download. <span class="fw-b">Please ensure all documents are uploaded into the correct folder before you proceed.</span>`,
              confirmButtonText: $localize`Send to client`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
        variants: {
          [Partners.LSOS]: {
            base: {
              create(quote) {
                return {
                  title: $localize`Send quote to client`,
                  content: $localize`You are about to inform <span class="fw-b">${quote.clientName}</span> their quote is ready to view. The client will receive an email informing them their quote is ready to view.</span>`,
                  confirmButtonText: $localize`Send to client`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
          [Partners.ACCA]: {
            base: {
              create(quote) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`You are about to release this quote to <span class="fw-b">${quote.clientName}</span>. If you choose to continue the client will receive an email providing them with their quote and payment options.</span>`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
        },
      },
      [DISPATCH_ACTIONS.Return]: {
        base: {
          create(quote) {
            return {
              title: $localize`Are you sure?`,
              content: $localize`You are returning the proposal form back to <span class="fw-b">${quote.clientName}</span> to allow them to make updates. The client will receive an email giving them access to edit the proposal form.`,
              confirmButtonText: $localize`Yes, continue`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
      },
      [DISPATCH_ACTIONS.Hold]: {
        base: {
          create(quote, cobMeta) {
            return {
              title: $localize`Are you sure?`,
              content: $localize`You are putting this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> on hold. To progress the quote you can either return the proposal to the client or action “Send quote to client”`,
              confirmButtonText: $localize`Yes, continue`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
      },
      [DISPATCH_ACTIONS.PaymentReceived]: {
        base: {
          create(quote, cobMeta) {
            return {
              title: $localize`Are you sure?`,
              content: $localize`By marking this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> as paid the policy will become active. The Client will be notified by email.`,
              confirmButtonText: $localize`Yes, continue`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
        variants: {
          [Partners.CONSTRUCTION]: {
            base: {
              create(quote, cobMeta) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By marking this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> as FON recieved the policy will become active. The Client will be notified by email.`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
        },
      },
      [DISPATCH_ACTIONS.Complete]: {
        base: {
          create(quote) {
            return {
              title: $localize`Send documents`,
              content: $localize`You are about to inform <span class="fw-b">${quote.clientName}</span> their policy documents are now ready to view. The client will receive an email informing them they can view their documents. <span class="fw-b">Please ensure all documents are uploaded into the policy documents folder before you proceed.</span>`,
              confirmButtonText: $localize`Send documents`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
        variants: {
          [Partners.NORWAY_PI]: {
            base: {
              create(quote, cobMeta) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By making this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> as complete the policy will become active. The client will be notified by email.</span>`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
          [Partners.LSOS]: {
            base: {
              create(quote, cobMeta) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By marking this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> as paid the policy will become active. The Client will be notified by email.`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
          [Partners.LSOS_ADDON]: {
            base: {
              create(quote) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By marking this <span class="fw-b">${quote.description}</span> quote for <span class="fw-b">${quote.clientName}</span> as complete the policy will become active. The client will receive an email informing them all policy documents are uploaded into the policy documents area ready for them to view and download. <span class="fw-b">Please ensure all documents are uploaded into the policy documents folder and made visible before you proceed.</span> If you need to amend the policy dates please do so via the “enquiries tab”.`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
          [Partners.ACCA]: {
            base: {
              create(quote, cobMeta) {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By marking this <span class="fw-b">${cobMeta?.title}</span> quote for <span class="fw-b">${quote.clientName}</span> as paid the policy will become active. The Client will be notified by email.`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
        },
      },
      [DISPATCH_ACTIONS.ProposalSubmit]: {
        base: {
          create() {
            return {
              title: $localize`Are you sure?`,
              content: $localize`You are submitting the proposal form with unaddressed referral rules in place. You will be able to review the quote before sending to the client.`,
              confirmButtonText: $localize`Yes, continue`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
      },
      [DISPATCH_ACTIONS.Accept]: {
        base: {},
        variants: {
          [Partners.ACCA]: {
            base: {
              create() {
                return {
                  title: $localize`Are you sure?`,
                  content: $localize`By proceeding with this quote <span class="fw-b">you will no longer be able to make any changes to your proposal or quote options</span>. If you are happy to proceed please continue to your payment options. Any questions please contact your consultant.`,
                  confirmButtonText: $localize`Yes, continue`,
                  cancelButtonText: $localize`Cancel`,
                };
              },
            },
          },
        },
      },
      [DISPATCH_ACTIONS.NotTakenUp]: {
        base: {
          create(quote) {
            return {
              title: $localize`Are you sure?`,
              content: $localize`By marking this <span class="fw-b">${quote.description}</span> quote as <span class="fw-b">“Not taken up”</span> you will not be able to revert this action or continue with the enquiry.`,
              confirmButtonText: $localize`Yes, continue`,
              cancelButtonText: $localize`Cancel`,
            };
          },
        },
      },
      TNC: {
        base: {},
        variants: {
          [Partners.ACCA]: {
            base: {
              create() {
                return {
                  title: $localize`Before you proceed`,
                  content: accaTncTemplate,
                  confirmButtonText: $localize`Agree & Proceed`,
                  dismissible: true,
                  disableUntilScrolledToBottom: true,
                };
              },
            },
          },
        },
      },
    },
  });

  constructor(private cobMetaService: CobMetaService) {}

  getMessageFor(quote: Quote, action: string) {
    return this.map([action, quote.portalId, quote.type]).create(
      quote,
      this.cobMetaService.getClassOfBusinessMeta(quote.classOfBusinessId)
    );
  }
}
